/**
 * Workspace panel model — ported from the PersonalWebsite "docview" system and
 * trimmed for GraphView. A panel is a document/note surface that can live in the
 * tab strip (Dockview), float freely, snap to an edge/corner, or maximize. The
 * workspace is driven off this state — never off DOM position — so a panel's
 * mode/geometry survive serialization and re-layout.
 */
export const PANEL_TYPES = ['home', 'files', 'doc', 'graph', 'orbit', 'ask', 'markdown'] as const;
export type PanelType = (typeof PANEL_TYPES)[number];

export type PanelMode =
  | 'tabbed'
  | 'floating'
  | 'snapped-left'
  | 'snapped-right'
  | 'snapped-top-left'
  | 'snapped-top-right'
  | 'snapped-bottom-left'
  | 'snapped-bottom-right'
  | 'maximized';

export type SnapRegion = 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface WorkspacePanelState {
  id: string;
  type: PanelType;
  title: string;
  mode: PanelMode;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  /** Snapshot to return to from a maximized/snapped mode. */
  previousState?: WorkspacePanelState | null;
  isActive: boolean;
  zIndex?: number;
  /** Lifted panel content (docId / kind / node_type) so it survives remounts. */
  data?: Record<string, unknown>;
}

export const SNAP_MODE: Record<SnapRegion, PanelMode> = {
  left: 'snapped-left',
  right: 'snapped-right',
  'top-left': 'snapped-top-left',
  'top-right': 'snapped-top-right',
  'bottom-left': 'snapped-bottom-left',
  'bottom-right': 'snapped-bottom-right',
};

export const isSnapped = (m: PanelMode): boolean => m.startsWith('snapped-');
export const isFloatingLike = (m: PanelMode): boolean => m === 'floating' || isSnapped(m);

/**
 * Floating-window z-index band. Panels climb from FLOATING_Z_BASE each time
 * they're focused (controller.ts's `nextZ()`); once the running counter
 * would cross FLOATING_Z_CAP the whole floating set is compacted back into
 * this band (relative order preserved) so it can never drift into the fixed
 * drop-preview/maximized tiers reserved above it. `zTop` is persisted, so
 * without this the counter would otherwise only ever grow across a
 * long-lived session. Full scale + rationale: workspace/DOCK_CONTRACT.md.
 */
export const FLOATING_Z_BASE = 10;
export const FLOATING_Z_CAP = 400;
